import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, ForbiddenError } from "@/api/errors";

import { FileDropZone } from "./file-drop-zone";
import { InlineConfirm } from "./inline-confirm";
import {
  canImportFormFiles,
  type FormFileIssue,
  type FormFileRow,
  markDuplicateSelections,
  readFormFile,
} from "./read-form-files";
import {
  ImportFormFilesError,
  useImportFormFiles,
} from "./use-import-form-files";
import { FORM_SCHEMA_ISSUE_KEYS } from "./validate-form-schema";

export type ImportFormFilesPanelProps = {
  readonly processModelId: string;
  /** モデルにあるフォーム定義の名前（同じ名前なら上書きになる）。 */
  readonly existing: readonly string[];
  readonly onClose: () => void;
  /**
   * 送信中かどうかを親に知らせる。送信中にこの欄を閉じられると、途中で失敗した
   * ときの理由を出す場所が無くなるので、親は閉じる導線を止める。
   */
  readonly onPendingChange?: (_pending: boolean) => void;
};

/** 確認欄の 1 行。同じ名前が 2 回並びうるので、名前でなく番号で見分ける。 */
type Entry = { readonly id: number; readonly row: FormFileRow };

/**
 * 「ファイルを取り込む」の確認欄。手元の `*-schema.json` / `*-uischema.json` を
 * 選んだ名前のままモデルに置く。
 *
 * 名前を変えないのは、BPMN が `formJsonSchemaFilename` でフォームを名前で指すため。
 * 「フォームを追加」で手入力した名前が AI の出力と 1 文字違うだけで、どのタスクからも
 * 参照されない孤児フォームになる。対になる 2 ファイルを一度に入れられるよう複数選べる。
 *
 * 1 ファイル 1 行で、新規か上書きかと使えない理由を出す。使えない行が 1 つでも残る間は
 * 確定させない（黙って一部だけ入れない）。途中で失敗しても入った分は残し、どこまで
 * 入ったかをこの中に出す（閉じると読み落とすため）。「取り込む」は赤にしない。
 *
 * 送信中は行を足す・外す・やめるを止める。送信中に mutation を reset すると、送信中の
 * 結果（失敗理由）が観測から外れ、同じ行を二重に送れてしまう。
 */
export function ImportFormFilesPanel({
  processModelId,
  existing,
  onClose,
  onPendingChange,
}: ImportFormFilesPanelProps) {
  const { t } = useTranslation("common");
  const importFiles = useImportFormFiles();
  const [entries, setEntries] = useState<readonly Entry[]>([]);
  const nextId = useRef(0);
  const isPending = importFiles.isPending;

  useEffect(() => {
    onPendingChange?.(isPending);
    return () => onPendingChange?.(false);
  }, [isPending, onPendingChange]);

  // 重複は行を外すたびに見直す（先の行を外せば後の行が使える）
  const markedRows = markDuplicateSelections(entries.map((entry) => entry.row));
  const marked = entries.map((entry, index) => ({
    id: entry.id,
    row: markedRows[index] ?? entry.row,
  }));

  const addFiles = async (files: readonly File[]) => {
    if (isPending) return;
    // 選び直したら前の失敗は当てはまらない
    importFiles.reset();
    const read = await Promise.all(
      files.map((file) => readFormFile(file, existing)),
    );
    setEntries((current) => [
      ...current,
      ...read.map((row) => ({ id: nextId.current++, row })),
    ]);
  };

  const removeEntry = (id: number) => {
    if (isPending) return;
    importFiles.reset();
    setEntries((current) => current.filter((entry) => entry.id !== id));
  };

  return (
    <InlineConfirm
      label={t("processDesign.importForms.title")}
      confirmLabel={t("processDesign.importForms.submit")}
      cancelLabel={t("processDesign.importForms.cancel")}
      canConfirm={canImportFormFiles(markedRows)}
      isPending={isPending}
      error={importFiles.isError ? errorMessage(importFiles.error) : null}
      onCancel={onClose}
      onConfirm={() => {
        // ボタンの disabled と同じ条件をここでも見る（送れるのは使える行だけ）
        if (!canImportFormFiles(markedRows)) return;
        void importFiles
          .mutateAsync({ processModelId, files: markedRows })
          .then(onClose, (error: unknown) => {
            // 入った行は一覧から外す（残るのは失敗した行とまだ送っていない行）。
            // 理由は importFiles.error でこの欄に出す
            if (error instanceof ImportFormFilesError) {
              setEntries((current) =>
                current.filter(
                  (entry) => !error.imported.includes(entry.row.fileName),
                ),
              );
            }
          });
      }}
    >
      <p className="m-0 font-medium">{t("processDesign.importForms.title")}</p>
      <p className="m-0">{t("processDesign.importForms.description")}</p>
      <FileDropZone
        label={t("processDesign.importForms.fileLabel")}
        accept=".json"
        multiple
        hint={t("processDesign.importForms.fileHint")}
        selectedName={null}
        issue={null}
        disabled={isPending}
        onFiles={(files) => void addFiles(files)}
      />
      {marked.length === 0 ? null : (
        <ul
          aria-label={t("processDesign.importForms.selected")}
          className="m-0 flex list-none flex-col gap-1 p-0"
        >
          {marked.map(({ id, row }) => (
            <li
              key={id}
              className="border-border flex flex-wrap items-baseline gap-2 rounded-md border px-3 py-2"
            >
              <span className="font-mono text-xs">{row.fileName}</span>
              {row.issue === null ? (
                <span className="text-muted-foreground text-xs">
                  {t(
                    row.action === "create"
                      ? "processDesign.importForms.create"
                      : "processDesign.importForms.overwrite",
                  )}
                </span>
              ) : (
                <span role="alert" className="text-destructive text-xs">
                  {issueMessage(row.issue)}
                </span>
              )}
              <button
                type="button"
                aria-label={t("processDesign.importForms.remove", {
                  name: row.fileName,
                })}
                disabled={isPending}
                className="text-muted-foreground hover:text-foreground hover:bg-muted ml-auto rounded-sm p-1 disabled:opacity-50"
                onClick={() => removeEntry(id)}
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </InlineConfirm>
  );

  function issueMessage(issue: FormFileIssue): string {
    switch (issue.kind) {
      case "bpmn":
        return t("processDesign.importForms.issueBpmn");
      case "not-json":
        return t("processDesign.importForms.issueNotJson");
      case "name":
        return t(
          issue.reason === "convention"
            ? "processDesign.newForm.issueConvention"
            : "processDesign.newForm.issueInvalid",
        );
      case "unreadable":
        return t("processDesign.fileDrop.unreadable");
      case "contents":
        return t(FORM_SCHEMA_ISSUE_KEYS[issue.reason], {
          detail: issue.detail ?? "",
        });
      case "duplicate":
        return t("processDesign.importForms.issueDuplicate");
    }
  }

  /** 止まった理由と、どこまで入ったか・まだ送っていないもの。 */
  function errorMessage(error: ImportFormFilesError): string {
    const separator = t("processDesign.importForms.nameSeparator");
    const parts = [describeCause(error.failed, error.cause)];
    if (error.imported.length > 0) {
      parts.push(
        t("processDesign.importForms.imported", {
          names: error.imported.join(separator),
        }),
      );
    }
    if (error.remaining.length > 0) {
      parts.push(
        t("processDesign.importForms.remaining", {
          names: error.remaining.join(separator),
        }),
      );
    }
    return parts.join(" ");
  }

  /**
   * 403 は権限。400 は backend の説明を添える（理由はそこにしか無い）。図の取り込みは
   * error_code（process_model_file_invalid）で絞るが、JSON のファイルで backend が返す
   * code は把握しきれていないので status で見る。
   */
  function describeCause(name: string, cause: unknown): string {
    if (cause instanceof ForbiddenError) {
      return t("processDesign.importForms.forbidden", { name });
    }
    if (cause instanceof ApiError && cause.status === 400) {
      return t("processDesign.importForms.fileInvalid", {
        name,
        detail: cause.message,
      });
    }
    return t("processDesign.importForms.failed", { name });
  }
}
